import { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import type { CloudAllData, UserData, ClaimedCoupon } from "@/types/cloud-data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatTimestamp } from "@/lib/utils";

interface AccountTabProps {
  cloudData: CloudAllData;
  onSaveCloud: (next: CloudAllData) => void;
}

type EditableField = "ruby" | "gold" | "questId";

const FIELD_MAX: Partial<Record<EditableField, number>> = {
  questId: 164,
};

function fmtTime(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

interface FieldRowProps {
  label: string;
  value: string;
  editing?: boolean;
  draft?: string;
  onDraftChange?: (v: string) => void;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  inputType?: "text" | "number";
  max?: number;
}

function FieldRow({
  label,
  value,
  editing,
  draft,
  onDraftChange,
  onEdit,
  onSave,
  onCancel,
  inputType = "text",
  max,
}: FieldRowProps) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm border-b border-border/50 last:border-0 gap-3">
      <span className="text-muted-foreground shrink-0">{label}</span>
      {editing ? (
        <div className="flex items-center gap-1">
          <Input
            type={inputType}
            value={draft ?? ""}
            onChange={(e) => onDraftChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSave?.();
              if (e.key === "Escape") onCancel?.();
            }}
            max={max}
            min={inputType === "number" ? 0 : undefined}
            autoFocus
            className="h-7 w-32 text-xs font-mono"
          />
          <Button size="icon" variant="ghost" onClick={onSave} className="h-7 w-7" title="Save">
            <Check className="h-3.5 w-3.5 text-emerald-400" />
          </Button>
          <Button size="icon" variant="ghost" onClick={onCancel} className="h-7 w-7" title="Cancel">
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <span className="text-foreground font-medium font-mono text-xs">{value}</span>
          {onEdit && (
            <Button
              size="icon"
              variant="ghost"
              onClick={onEdit}
              className="h-6 w-6 opacity-60 hover:opacity-100"
              title="Edit"
            >
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function AccountTab({ cloudData, onSaveCloud }: AccountTabProps) {
  const userData: UserData = cloudData.userData;
  const claimedCoupons = ((userData.claimedCoupons as ClaimedCoupon[]) ?? []);

  const [editing, setEditing] = useState<EditableField | null>(null);
  const [draft, setDraft] = useState<string>("");

  function startEdit(field: EditableField) {
    setEditing(field);
    setDraft(String(userData[field] ?? 0));
  }

  function cancelEdit() {
    setEditing(null);
    setDraft("");
  }

  function commitEdit() {
    if (!editing) return;
    let n = Number(draft);
    if (!Number.isFinite(n)) {
      cancelEdit();
      return;
    }
    n = Math.max(0, Math.floor(n));
    const max = FIELD_MAX[editing];
    if (max !== undefined) n = Math.min(n, max);
    if (n === userData[editing]) {
      cancelEdit();
      return;
    }
    onSaveCloud({
      ...cloudData,
      userData: { ...userData, [editing]: n },
    });
    cancelEdit();
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-card p-4 space-y-1">
          <h3 className="text-sm font-medium text-muted-foreground mb-2">General</h3>
          <FieldRow
            label="Ruby"
            value={userData.ruby.toLocaleString()}
            editing={editing === "ruby"}
            draft={draft}
            onDraftChange={setDraft}
            onEdit={() => startEdit("ruby")}
            onSave={commitEdit}
            onCancel={cancelEdit}
            inputType="number"
          />
          <FieldRow
            label="Gold"
            value={userData.gold.toLocaleString()}
            editing={editing === "gold"}
            draft={draft}
            onDraftChange={setDraft}
            onEdit={() => startEdit("gold")}
            onSave={commitEdit}
            onCancel={cancelEdit}
            inputType="number"
          />
          <FieldRow label="Play Time" value={fmtTime(userData.playTime)} />
          <FieldRow
            label="Quest ID"
            value={String(userData.questId)}
            editing={editing === "questId"}
            draft={draft}
            onDraftChange={setDraft}
            onEdit={() => startEdit("questId")}
            onSave={commitEdit}
            onCancel={cancelEdit}
            inputType="number"
            max={FIELD_MAX.questId}
          />
          <FieldRow label="Last Checkpoint" value={userData.lastCheckpoint || "—"} />
        </div>

        <div className="rounded-lg border border-border bg-card p-4 space-y-1">
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Status</h3>
          <FieldRow label="Premium" value={userData.premiumUser ? "Yes" : "No"} />
          <FieldRow label="First Time Play" value={userData.firstTimePlay ? "Yes" : "No"} />
          <FieldRow label="Device ID" value={userData.deviceID || "—"} />
          <FieldRow label="Active Feast" value={userData.activeFeastBuff || "—"} />
          <FieldRow label="Feast Duration" value={`${(userData.feastDuration ?? 0).toFixed(1)}s`} />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground mb-2">Monthly Ruby Pass</h3>
        <div className="space-y-1 text-sm">
          <FieldRow label="Last Claim" value={formatTimestamp(userData.welkinStatus?.lastClaim)} />
          <FieldRow label="Valid Until" value={formatTimestamp(userData.welkinStatus?.validUntil)} />
        </div>
      </div>

      {claimedCoupons.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Claimed Coupons ({claimedCoupons.length})</h3>
          <div className="space-y-1">
            {claimedCoupons.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <code className="text-xs font-mono">{c.couponId}</code>
                <span className="text-muted-foreground text-xs">{formatTimestamp(c.claimedAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
